import { supabase } from "../lib/supabase";

// --- Types & Interfaces ---

export interface Activity {
  id?: string;
  student_id?: string;
  type: string;
  position: string;
  organization: string;
  description: string;
  participation_grade_levels: string[];
  timing_of_participation: string[];
  hours_per_week: number;
  weeks_per_year: number;
  intend_to_participate_in_college: boolean | null;
  created_at?: string;
  updated_at?: string;
  order_index?: number;
}

// --- Validation Functions ---

export interface ValidationError {
  field: string;
  message: string;
}
export function validateActivity(
  activity: Partial<Activity>,
  isPartial: boolean = false
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Type validation
  //yes if i am in strict mode (!isPartial || activity.type !== undefined) or if the type filed exist in the database
  //if it is missing or not a string push error and then check if the text is too long( > 100)
  if (!isPartial || activity.type !== undefined) {
    if (!activity.type || typeof activity.type !== "string") {
      errors.push({ field: "type", message: "Activity type is required" });
    } else if (activity.type.length > 100) {
      errors.push({
        field: "type",
        message: "Activity type cannot exceed 100 characters",
      });
    }
  }

  // Position validation
  //yes if i am in strict mode (!isPartial || activity.position !== undefined) or if the position filed exist in the database
  //if it is missing or not a string push error and then check if the text is too long( > 100)
  if (!isPartial || activity.position !== undefined) {
    if (!activity.position || typeof activity.position !== "string") {
      errors.push({ field: "position", message: "Position is required" });
    } else if (activity.position.length > 100) {
      errors.push({
        field: "position",
        message: "Position cannot exceed 100 characters",
      });
    }
  }

  // Organization validation
  if (!isPartial || activity.organization !== undefined) {
    if (!activity.organization || typeof activity.organization !== "string") {
      errors.push({
        field: "organization",
        message: "Organization is required",
      });
    } else if (activity.organization.length > 100) {
      errors.push({
        field: "organization",
        message: "Organization cannot exceed 100 characters",
      });
    }
  }

  // Description validation
  if (!isPartial || activity.description !== undefined) {
    if (!activity.description || typeof activity.description !== "string") {
      errors.push({ field: "description", message: "Description is required" });
    } else if (activity.description.length > 150) {
      errors.push({
        field: "description",
        message: "Description cannot exceed 150 characters",
      });
    }
  }

  // Grade levels validation
  if (!isPartial || activity.participation_grade_levels !== undefined) {
    if (!Array.isArray(activity.participation_grade_levels)) {
      errors.push({
        field: "participation_grade_levels",
        message: "Participation grade levels must be an array",
      });
    }
  }

  // Timing validation
  if (!isPartial || activity.timing_of_participation !== undefined) {
    if (!Array.isArray(activity.timing_of_participation)) {
      errors.push({
        field: "timing_of_participation",
        message: "Timing of participation must be an array",
      });
    }
  }

  // Hours per week validation
  if (!isPartial || activity.hours_per_week !== undefined) {
    const hours = Number(activity.hours_per_week);
    if (isNaN(hours) || hours < 0 || hours > 168) {
      errors.push({
        field: "hours_per_week",
        message: "Hours per week must be between 0 and 168",
      });
    }
  }

  // Weeks per year validation
  if (!isPartial || activity.weeks_per_year !== undefined) {
    const weeks = Number(activity.weeks_per_year);
    if (isNaN(weeks) || weeks < 0 || weeks > 52) {
      errors.push({
        field: "weeks_per_year",
        message: "Weeks per year must be between 0 and 52",
      });
    }
  }
  return errors;
}

function sanitizeActivity(activity: Partial<Activity>): Partial<Activity> {
  const sanitized: Partial<Activity> = {};
  //if the type is not undefined then santinize it by removing any leading or trailing whitespace and then taking the first 100 characters
  if (activity.type !== undefined) {
    sanitized.type = String(activity.type).trim().substring(0, 100);
  }
  //if the position is not undefined then santinize it by removing any leading or trailing whitespace and then taking the first 100 characters
  if (activity.position !== undefined) {
    sanitized.position = String(activity.position).trim().substring(0, 100);
  }
  //if the organization is not undefined then santinize it by removing any leading or trailing whitespace and then taking the first 100 characters
  if (activity.organization !== undefined) {
    sanitized.organization = String(activity.organization)
      .trim()
      .substring(0, 100);
  }
  //if the description is not undefined then santinize it by removing any leading or trailing whitespace and then taking the first 150 characters
  if (activity.description !== undefined) {
    sanitized.description = String(activity.description)
      .trim()
      .substring(0, 150);
  }
  //if the participation_grade_levels is not undefined then santinize by checki if it is an array and then filtering out of any non string values
  if (activity.participation_grade_levels !== undefined) {
    sanitized.participation_grade_levels = Array.isArray(
      activity.participation_grade_levels
    )
      ? activity.participation_grade_levels.filter(
        (item) => typeof item === "string"
      )
      : [];
  }
  //if the timing_of_participation is not undefinded then santinize by checking if it is an array and then filtering out of any non string values
  if (activity.timing_of_participation !== undefined) {
    sanitized.timing_of_participation = Array.isArray(
      activity.timing_of_participation
    )
      ? activity.timing_of_participation.filter(
        (item) => typeof item === "string"
      )
      : [];
  }
  if (activity.hours_per_week !== undefined) {
    const hours = Number(activity.hours_per_week);
    sanitized.hours_per_week = isNaN(hours)
      ? 0
      : Math.max(0, Math.min(168, Math.floor(hours)));
  }
  if (activity.weeks_per_year !== undefined) {
    const weeks = Number(activity.weeks_per_year);
    sanitized.weeks_per_year = isNaN(weeks)
      ? 0
      : Math.max(0, Math.min(52, Math.floor(weeks)));
  }
  if (activity.intend_to_participate_in_college !== undefined) {
    sanitized.intend_to_participate_in_college =
      activity.intend_to_participate_in_college === null
        ? null
        : Boolean(activity.intend_to_participate_in_college);
  }

  return sanitized;
}

// --- API Functions ---

/**
 * Fetch all activities for the current user
 */
export async function getActivities(): Promise<Activity[]> {
  const { data, error } = await supabase
    .from("activities")
    .select("*")
    .order("order_index", { ascending: true });

  if (error) {
    console.error("Error fetching activities:", error);
    throw new Error("Failed to load activities");
  }

  return data.map((item) => ({
    ...item,
    participation_grade_levels: item.participation_grade_levels || [],
    timing_of_participation: item.timing_of_participation || [],
  })) as Activity[];
}

/**
 * Create a new activity
 */
export async function createActivity(
  studentId: string,
  activity: Omit<
    Activity,
    "id" | "student_id" | "created_at" | "updated_at" | "order_index"
  >
): Promise<Activity> {
  const errors = validateActivity(activity, false);
  if (errors.length > 0) {
    throw new Error(
      `Validation failed: ${errors.map((e) => e.message).join(", ")}`
    );
  }
  const sanitized = sanitizeActivity(activity);
  const { data: last } = await supabase
    .from("activities")
    .select("order_index")
    .eq("student_id", studentId)
    .order("order_index", { ascending: false })
    .limit(1)
    .maybeSingle();
  const newOrderIndex = last ? last.order_index + 1 : 0;
  const { data, error } = await supabase
    .from("activities")
    .insert({
      ...sanitized,
      student_id: studentId,
      order_index: newOrderIndex,
    })
    .select()
    .single();
  if (error) throw error;
  return data as Activity;
}

/**
 * Update an existing activity
 */
export async function updateActivity(
  studentId: string,
  id: string,
  updates: Partial<
    Omit<Activity, "id" | "student_id" | "created_at" | "updated_at">
  >
): Promise<Activity> {
  // Validate partial updates
  const errors = validateActivity(updates, true);
  if (errors.length > 0) {
    throw new Error(
      `Validation failed: ${errors.map((e) => e.message).join(", ")}`
    );
  }

  // Sanitize input
  const sanitized = sanitizeActivity(updates);

  const { data, error } = await supabase
    .from("activities")
    .update(sanitized)
    .eq("id", id)
    .eq("student_id", studentId)
    .select()
    .single();

  if (error) {
    console.error("Error updating activity:", error);
    throw new Error("Failed to update activity");
  }

  return data as Activity;
}

/**
 * Delete an activity
 */
export async function deleteActivity(
  studentId: string,
  id: string
): Promise<void> {
  const { error } = await supabase
    .from("activities")
    .delete()
    .eq("id", id)
    .eq("student_id", studentId);
  if (error) {
    console.error("Error deleting activity:", error);
    throw new Error("Failed to delete activity");
  }
}
/**
 * Update an activity order
 */
export async function updateActivityOrder(
  studentId: string,
  id: string,
  order_index: number
): Promise<void> {
  const { error } = await supabase
    .from("activities")
    .update({ order_index })
    .eq("id", id)
    .eq("student_id", studentId);
  if (error) {
    console.error("Error updating activity order:", error);
    throw new Error("Failed to update activity order");
  }
}

// --- Dashboard Helpers ---

export interface ActivityCategories {
  'Academic & Intellectual': number;
  'Arts & Culture': number;
  'Leadership & Community': number;
  'Work & Family': number;
  'Athletics': number;
  'Other': number;
}

export interface ActivityDashboardSummary {
  count: number; // Total activities (usually out of 10)
  categories: ActivityCategories; // Breakdown by category
}

/**
 * Get a categorized summary of activities for the dashboard
 */
export async function getActivityDashboardSummary(studentId: string): Promise<ActivityDashboardSummary> {
  const { data, error } = await supabase
    .from("activities")
    .select("type")
    .eq("student_id", studentId);

  if (error) {
    console.error("Error fetching activity summary:", error);
    // Return empty state on error to prevent dashboard crash
    return {
      count: 0,
      categories: {
        'Academic & Intellectual': 0,
        'Arts & Culture': 0,
        'Leadership & Community': 0,
        'Work & Family': 0,
        'Athletics': 0,
        'Other': 0
      }
    };
  }

  const summary = data.reduce((acc, activity) => {
    let category: keyof ActivityCategories = 'Other';
    const type = activity.type;

    if (["Academic", "Computer/Technology", "Debate/Speech", "Journalism/Publication", "Research", "Robotics", "Science/Math"].includes(type)) {
      category = 'Academic & Intellectual';
    } else if (["Art", "Cultural", "Dance", "Music: Instrumental", "Music: Vocal", "Theater/Drama"].includes(type)) {
      category = 'Arts & Culture';
    } else if (["Community Service (Volunteer)", "Environmental", "Foreign Exchange", "Junior ROTC", "LGBT", "Religious", "School Spirit", "Student Govt./Politics"].includes(type)) {
      category = 'Leadership & Community';
    } else if (["Career Oriented", "Family Responsibilities", "Work (Paid)"].includes(type)) {
      category = 'Work & Family';
    } else if (["Athletics: Club", "Athletics: JV/Varsity"].includes(type)) {
      category = 'Athletics';
    }

    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {
    'Academic & Intellectual': 0,
    'Arts & Culture': 0,
    'Leadership & Community': 0,
    'Work & Family': 0,
    'Athletics': 0,
    'Other': 0
  } as ActivityCategories);

  return {
    count: data.length,
    categories: summary
  };
}
